import { findProceduresByTerm } from '@/app/(main)/_components/search-modal/_services/search-api/find-procedures-by-term.service';
import { findProfessionalsByTerm } from '@/app/(main)/_components/search-modal/_services/search-api/find-professionals-by-term.service';
import { findPartnerFacilitiesByTerm } from '@/app/(main)/_components/search-modal/_services/search-api/find-partner-facilities-by-term.service';
import {
  mapFacilitiesToSearchResults,
  mapProcedureToSearchResult,
  mapProfessionalToSearchResult,
} from '@/app/(main)/_components/search-modal/_services/search-api/map-to-search-result.util';
import { RESULTS_PAGE_SIZE, SECTION_RESULT_LIMIT } from '@/app/(main)/_components/search-modal/_consts/search-results.consts';
import {
  AllSearchResults,
  SearchCategoryResults,
  SearchType,
} from '@/app/(main)/_components/search-modal/search-modal.types';

type CategorizedResults = Omit<AllSearchResults, SearchType.ALL>;

interface FetchResultsByTypeParams {
  term: string;
  citySlug?: string;
  searchType: SearchType;
  page?: number;
  limit?: number;
}

interface SearchApiPageParams {
  term: string;
  citySlug?: string;
  page: number;
  limit: number;
}

interface SearchApiProcedurePageParams extends SearchApiPageParams {
  categories?: string[];
}

interface FetchProceduresByCategoryParams {
  term: string;
  citySlug?: string;
  category: SearchType;
  page?: number;
  limit?: number;
}

function toEveryCategoryBucket(categories: CategorizedResults): SearchCategoryResults {
  return {
    results: [
      ...categories[SearchType.PROCEDURE].results,
      ...categories[SearchType.PROFESSIONAL].results,
      ...categories[SearchType.PARTNER].results,
    ],
    hasMore: Object.values(categories).some((category) => category.hasMore),
  };
}

async function fetchProceduresFromSearchApi(params: SearchApiProcedurePageParams): Promise<SearchCategoryResults> {
  const { procedures, hasMore } = await findProceduresByTerm(params);

  return { results: procedures.map(mapProcedureToSearchResult), hasMore };
}

async function fetchProfessionalsFromSearchApi(params: SearchApiPageParams): Promise<SearchCategoryResults> {
  const { professionals, hasMore } = await findProfessionalsByTerm(params);

  return { results: professionals.map(mapProfessionalToSearchResult), hasMore };
}

async function fetchPartnersFromSearchApi(params: SearchApiPageParams): Promise<SearchCategoryResults> {
  const { facilities, hasMore } = await findPartnerFacilitiesByTerm(params);

  return { results: mapFacilitiesToSearchResults(facilities), hasMore };
}

async function fetchCategoriesFromSearchApi(params: SearchApiPageParams): Promise<CategorizedResults> {
  const [procedures, professionals, partners] = await Promise.all([
    fetchProceduresFromSearchApi(params),
    fetchProfessionalsFromSearchApi(params),
    fetchPartnersFromSearchApi(params),
  ]);

  return {
    [SearchType.PROCEDURE]: procedures,
    [SearchType.PROFESSIONAL]: professionals,
    [SearchType.PARTNER]: partners,
  };
}

async function fetchPageFromSearchApi(
  searchType: SearchType,
  params: SearchApiPageParams,
): Promise<SearchCategoryResults> {
  switch (searchType) {
    case SearchType.PROCEDURE:
      return fetchProceduresFromSearchApi(params);
    case SearchType.PROFESSIONAL:
      return fetchProfessionalsFromSearchApi(params);
    case SearchType.PARTNER:
      return fetchPartnersFromSearchApi(params);
    default:
      return toEveryCategoryBucket(await fetchCategoriesFromSearchApi(params));
  }
}

export async function fetchCategorizedResults(term: string, citySlug?: string): Promise<AllSearchResults> {
  const categories = await fetchCategoriesFromSearchApi({ term, citySlug, page: 1, limit: SECTION_RESULT_LIMIT });

  return {
    ...categories,
    [SearchType.ALL]: toEveryCategoryBucket(categories),
  };
}

export function fetchResultsByType({
  term,
  searchType,
  page = 1,
  limit = RESULTS_PAGE_SIZE,
  citySlug,
}: FetchResultsByTypeParams): Promise<SearchCategoryResults> {
  return fetchPageFromSearchApi(searchType, { term, citySlug, page, limit });
}

export function fetchProceduresByCategory({
  term,
  category,
  page = 1,
  limit = RESULTS_PAGE_SIZE,
  citySlug,
}: FetchProceduresByCategoryParams): Promise<SearchCategoryResults> {
  return fetchProceduresFromSearchApi({ term, citySlug, categories: [category], page, limit });
}
